import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { App as AntApp, Skeleton, Spin } from 'antd';
import dayjs from 'dayjs';
import * as salesApi from '../../api/sales';
import NoteItem from './NoteItem';
import NoteActionSheet from './NoteActionSheet';
import RichNoteEditorLazy from './RichNoteEditorLazy';
import useSheetAnim from '../../hooks/useSheetAnim';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import './notes.css';

// Full-height notes screen. Reads as a conversation: oldest at the top, newest at
// the bottom, opening pinned to the latest, and scrolling UP pulls in older pages.
//
// The server paginates newest-first (page 1 = the 20 most recent), which is the
// right thing for it to do — the admin drawer wants that order directly. Here each
// page is reversed for display and older pages are prepended, with the scroll
// position anchored so the thread doesn't jump under your thumb.

const PAGE_SIZE = 20;
const TOP_THRESHOLD = 140; // px from the top that triggers loading older notes
const MAX_FILES = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const GROUP_WINDOW_MS = 5 * 60 * 1000; // same author within 5 min reads as one turn

const dayLabel = (ts) => {
  const d = dayjs(ts);
  if (d.isSame(dayjs(), 'day')) return 'Today';
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.isSame(dayjs(), 'year') ? d.format('ddd, D MMM') : d.format('D MMM YYYY');
};

/**
 * Flatten the server's parent→replies tree into the single chronological run a
 * conversation reads as, and mark up day breaks and message grouping.
 *
 * Replies follow their parent rather than being sorted purely by time: the
 * server pages TOP-LEVEL notes, so a reply's parent is always in the same page,
 * whereas global time-sorting could drop a recent reply into a page of history.
 */
function buildTimeline(notes) {
  const flat = [];
  for (const n of notes) {
    flat.push({ note: n, parent: null });
    for (const r of n.replies || []) flat.push({ note: r, parent: n });
  }

  const rows = [];
  let lastDay = null;
  let prev = null;
  for (const item of flat) {
    const day = dayjs(item.note.created_at).format('YYYY-MM-DD');
    let dayBroke = false;
    if (day !== lastDay) {
      rows.push({ kind: 'day', key: `day-${day}-${item.note.id}`, label: dayLabel(item.note.created_at) });
      lastDay = day;
      dayBroke = true;
    }

    const p = prev?.note;
    const grouped = !dayBroke
      && !!p
      && !p.is_system
      && !item.note.is_system
      && !item.parent // a reply always re-states who it answers
      && p.author_id === item.note.author_id
      && !!p.mine === !!item.note.mine
      && new Date(item.note.created_at) - new Date(p.created_at) < GROUP_WINDOW_MS;

    rows.push({ kind: 'note', key: item.note.id, item, grouped });
    prev = item;
  }
  return rows;
}

export default function SaleNotesSheet({ saleId, saleRef, open, onClose, onReadAll, onResourceClick, onOpenFile }) {
  // Stays mounted one beat past `open` so the sheet can slide back down.
  const { mounted: sheetMounted, closing: sheetClosing } = useSheetAnim(open);
  const { message, modal } = AntApp.useApp();
  const [notes, setNotes] = useState([]); // ascending — oldest first, newest last
  const [total, setTotal] = useState(null);
  const [users, setUsers] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const pageRef = useRef(1);
  const listRef = useRef(null);

  // Scroll bookkeeping. Before prepending older notes we record where the topmost
  // rendered note sits on screen, and afterwards put it back exactly there.
  // Anchoring to an ELEMENT rather than to scrollHeight matters: the "loading
  // earlier" skeleton is itself in the flow, so a height delta would be off by
  // the skeleton's height and the thread would jump by that much.
  const pendingRestoreRef = useRef(null);
  const pinnedToBottomRef = useRef(false);
  // Mirrors `loadingOlder` but updates synchronously. Momentum scrolling fires a
  // burst of scroll events, and a state flag hasn't committed yet when the second
  // one arrives — several pages would load at once and page numbers would skip.
  const loadingOlderRef = useRef(false);
  // Whether the reader is sitting on the latest note. Tracked on scroll so the
  // composer resize below can tell "keep me pinned to the newest" apart from
  // "I've scrolled up to read history, leave me alone".
  const nearBottomRef = useRef(true);
  const composerRef = useRef(null);

  // Composer
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState({ json: null, text: '' });
  const [files, setFiles] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const fileRef = useRef(null);

  // Row-level state the ··· sheet drives
  const [actionNote, setActionNote] = useState(null);
  const [editingId, setEditingId] = useState(null);

  // Held in a ref so a parent that re-creates the callback each render can't
  // retrigger the load effect below.
  const readCbRef = useRef(onReadAll);
  readCbRef.current = onReadAll;

  const timeline = useMemo(() => buildTimeline(notes), [notes]);

  /** mode: 'replace' (jump to the latest) | 'prepend' (older history on top) */
  const fetchPage = useCallback(async (page, mode) => {
    if (mode === 'prepend') setLoadingOlder(true); else setLoading(true);
    try {
      const res = await salesApi.listSalesNotes(saleId, { page, limit: PAGE_SIZE });
      const pagination = res?.pagination;
      const rows = (res?.data || []).slice().reverse(); // newest-first → oldest-first

      if (mode === 'prepend') {
        const el = listRef.current;
        const anchor = el?.querySelector('[data-note-id]');
        if (anchor) {
          pendingRestoreRef.current = { id: anchor.dataset.noteId, top: anchor.getBoundingClientRect().top };
        }
        setNotes((prev) => [...rows, ...prev]);
      } else {
        pinnedToBottomRef.current = true; // land on the newest note
        setNotes(rows);
        if (pagination) setTotal(pagination.total);
      }

      setHasOlder(pagination ? pagination.page < pagination.pages : rows.length === PAGE_SIZE);
    } catch (e) {
      message.error(e.message || 'Could not load the notes');
    } finally {
      setLoading(false);
      setLoadingOlder(false);
      loadingOlderRef.current = false;
    }
  }, [saleId, message]);

  useEffect(() => {
    if (!open) return;
    pageRef.current = 1;
    loadingOlderRef.current = false;
    setHasOlder(true);
    fetchPage(1, 'replace');
    salesApi.listSalesNotesMentionableUsers(saleId).then((r) => setUsers(r?.data || [])).catch(() => {});
    salesApi.listSalesNotesMentionableResources(saleId).then((r) => setResources(r?.data || [])).catch(() => {});
    // Opening the thread IS the read event — it lands on the newest note. Clear
    // the badge optimistically so it never lingers on a thread you're reading.
    readCbRef.current?.();
    salesApi.createSalesNotesRead(saleId).catch(() => {});
  }, [open, saleId, fetchPage]);

  // Apply the scroll intent recorded above — before paint, so neither the jump to
  // the newest note nor the anchoring after a prepend is ever visible.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;

    if (pendingRestoreRef.current) {
      const { id, top } = pendingRestoreRef.current;
      pendingRestoreRef.current = null;
      const anchor = el.querySelector(`[data-note-id="${CSS.escape(id)}"]`);
      if (anchor) el.scrollTop += anchor.getBoundingClientRect().top - top;
      return;
    }
    if (pinnedToBottomRef.current) {
      pinnedToBottomRef.current = false;
      el.scrollTop = el.scrollHeight;
      nearBottomRef.current = true;
    }
  }, [notes]);

  // The composer grows after it opens — the rich editor is code-split, so it
  // mounts a frame or two late, and staged files and the reply chip change its
  // height too. A one-shot scroll fires before all that settles, so watch the
  // composer and keep the newest note in view while it moves.
  useEffect(() => {
    const list = listRef.current;
    const composer = composerRef.current;
    if (!list || !composer || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (nearBottomRef.current) list.scrollTop = list.scrollHeight;
    });
    ro.observe(composer);
    return () => ro.disconnect();
    // Keyed on `sheetMounted`, NOT `open`: the sheet renders null for one beat
    // after `open` flips while the enter animation state settles, so an effect
    // keyed on `open` fires before the composer exists and observes nothing.
  }, [sheetMounted]);

  const loadOlder = useCallback(() => {
    if (loadingOlderRef.current) return;
    loadingOlderRef.current = true;
    pageRef.current += 1;
    fetchPage(pageRef.current, 'prepend');
  }, [fetchPage]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.clientHeight - el.scrollTop < 80;
    if (loading || !hasOlder) return;
    if (el.scrollTop < TOP_THRESHOLD) loadOlder();
  };

  // A short first page can't be scrolled, so the handler above would never fire —
  // keep pulling history until the thread fills the screen or runs out.
  useEffect(() => {
    const el = listRef.current;
    if (!el || loading || !hasOlder) return;
    if (el.scrollHeight <= el.clientHeight) loadOlder();
  }, [notes, loading, hasOlder, loadOlder]);

  // Opening the composer shrinks the list; jump to the latest note and let the
  // observer above hold it there as the editor finishes mounting.
  useEffect(() => {
    if (!composing) return;
    const el = listRef.current;
    if (!el) return;
    nearBottomRef.current = true;
    el.scrollTop = el.scrollHeight;
  }, [composing]);

  // ── Files staged in the composer ────────────────────────────────────────
  const pickFiles = (e) => {
    const picked = [...(e.target.files || [])];
    e.target.value = '';
    const room = MAX_FILES - files.length;
    if (room <= 0) { message.warning(`Up to ${MAX_FILES} files per note`); return; }
    const next = [];
    for (const f of picked.slice(0, room)) {
      if (f.size > MAX_FILE_BYTES) { message.warning(`${f.name} is over ${MAX_FILE_BYTES / 1024 / 1024}MB`); continue; }
      next.push({
        uid: `${f.name}-${f.size}-${f.lastModified}`,
        file: f,
        isImage: f.type?.startsWith('image/'),
        preview: f.type?.startsWith('image/') ? URL.createObjectURL(f) : null,
      });
    }
    setFiles((prev) => [...prev, ...next]);
  };

  const dropFile = (uid) => setFiles((prev) => {
    const f = prev.find((x) => x.uid === uid);
    if (f?.preview) URL.revokeObjectURL(f.preview);
    return prev.filter((x) => x.uid !== uid);
  });

  // Revoke image previews on unmount only — keyed on `files` it would revoke
  // URLs that are still on screen every time a file is staged.
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => () => { filesRef.current.forEach((f) => f.preview && URL.revokeObjectURL(f.preview)); }, []);

  /** Signed-URL upload flow, one file at a time (mirrors the sale-document flow). */
  const uploadFiles = async (noteId, staged) => {
    for (const item of staged) {
      const f = item.file;
      try {
        const urlRes = await salesApi.createSalesNoteAttachmentUploadUrl(saleId, noteId, {
          fileName: f.name, fileSize: f.size, contentType: f.type,
        });
        const { uploadUrl, path: storagePath } = urlRes?.data || {};
        if (!uploadUrl) throw new Error('No upload URL');
        await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': f.type || 'application/octet-stream' }, body: f });
        await salesApi.createSalesNoteAttachmentConfirm(saleId, noteId, {
          storagePath, fileName: f.name, fileSize: f.size, contentType: f.type,
        });
      } catch (err) {
        message.error(`Could not attach ${f.name}`);
      }
    }
  };

  const closeComposer = () => {
    files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
    setFiles([]);
    setDraft({ json: null, text: '' });
    setReplyTo(null);
    setComposing(false);
    setResetSignal((n) => n + 1);
  };

  const startReply = (note) => {
    setReplyTo(note);
    setComposing(true);
  };

  // An unsent note: words in the composer or files staged on it. The editor is
  // unmounted with the sheet, so closing it — the chevron, a /resource link that
  // jumps to a tab — loses the note as surely as leaving the sale does; every one
  // of those exits asks first, and clears the composer once the agent agrees.
  const unsent = composing && ((draft.text || '').trim().length > 0 || files.length > 0);
  const guard = useUnsavedChanges(open && unsent, { what: 'your note' });
  const closeSheet = () => guard.confirmClose(() => { closeComposer(); onClose(); });
  const openResource = (...args) => guard.confirmClose(() => { closeComposer(); onResourceClick?.(...args); });

  // ── Post / reply ────────────────────────────────────────────────────────
  const post = async () => {
    const text = (draft.text || '').trim();
    if (posting || (!text && files.length === 0)) return;
    setPosting(true);
    try {
      const payload = { content: text, content_json: draft.json };
      if (replyTo) payload.parent_note_id = replyTo.id;
      const res = await salesApi.createSalesNotesById(saleId, payload);
      const noteId = res?.data?.id;

      if (noteId && files.length) await uploadFiles(noteId, files);

      closeComposer();
      // Back to the newest page so a reply lands under its parent and attachments
      // come back with signed URLs; the layout effect drops us at the bottom.
      pageRef.current = 1;
      setHasOlder(true);
      await fetchPage(1, 'replace');
    } catch (e) {
      message.error(e.message || 'Could not post the note');
    } finally {
      setPosting(false);
    }
  };

  // ── Edit / delete / react — patch in place, no full reload ──────────────
  const patchNote = (noteId, patch) => setNotes((prev) => prev.map((n) => {
    if (n.id === noteId) return { ...n, ...patch };
    if (n.replies?.length) {
      return { ...n, replies: n.replies.map((r) => (r.id === noteId ? { ...r, ...patch } : r)) };
    }
    return n;
  }));

  const saveEdit = async (noteId, data) => {
    try {
      const res = await salesApi.updateSalesNotesByIdNoteId(saleId, noteId, data);
      patchNote(noteId, res?.data || { ...data, is_edited: true });
      setEditingId(null);
      message.success('Note updated');
    } catch (e) {
      message.error(e.message || 'Could not update the note');
      throw e;
    }
  };

  const deleteNote = (note) => {
    modal.confirm({
      title: 'Delete this note?',
      content: 'HQ will see a “this comment has been deleted” placeholder in its place.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      centered: true,
      onOk: async () => {
        try {
          await salesApi.deleteSalesNotesByIdNoteId(saleId, note.id);
          patchNote(note.id, { is_deleted: true, content: 'This comment has been deleted', content_json: null, reactions: {}, attachments: [] });
          message.success('Note deleted');
        } catch (e) {
          message.error(e.message || 'Could not delete the note');
        }
      },
    });
  };

  const toggleReaction = async (noteId, reaction, reacted) => {
    try {
      const res = reacted
        ? await salesApi.deleteSalesNoteReaction(saleId, noteId, reaction)
        : await salesApi.createSalesNoteReaction(saleId, noteId, reaction);
      patchNote(noteId, { reactions: res?.data || {} });
    } catch (e) {
      message.error(e.message || 'Could not update the reaction');
    }
  };

  const copyNote = async (note) => {
    try {
      await navigator.clipboard.writeText(note.content || '');
      message.success('Copied');
    } catch {
      message.error('Could not copy — your browser blocked clipboard access');
    }
  };

  if (!sheetMounted) return null;

  const canPost = !posting && ((draft.text || '').trim().length > 0 || files.length > 0);

  return (
    <div className={`sheet-panel${sheetClosing ? ' is-closing' : ''}`} style={{ position: 'fixed', inset: 0, zIndex: 4500, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', paddingTop: 'calc(10px + env(safe-area-inset-top))', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div onClick={closeSheet} style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Notes</div>
          <div style={{ fontSize: 10.5, color: 'var(--muted)' }}>
            {saleRef}{total != null ? ` · ${total} ${total === 1 ? 'entry' : 'entries'}` : ''}
          </div>
        </div>
      </div>

      <div className="note-thread-wrap">
        {/* Floats OVER the thread rather than sitting in it — an indicator in the
            flow would shift every row while it showed, and the scroll anchoring
            would then restore to that shifted position. */}
        {loadingOlder && (
          <div className="note-loading-older"><Spin size="small" /> Loading earlier notes…</div>
        )}

        <div ref={listRef} onScroll={handleScroll} className="scrx note-thread">
        {loading ? (
          <div style={{ padding: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></div>
        ) : notes.length === 0 ? (
          <div className="note-empty">Nothing here yet. Start the thread with HQ below.</div>
        ) : (
          <>
            <div className="note-thread-spacer" />
            {/* History arrives at the TOP — older notes scroll into view above. */}
            {!hasOlder && !loadingOlder && <div className="note-thread-start">Start of the thread</div>}

            {timeline.map((row) => (row.kind === 'day' ? (
              <div key={row.key} className="note-day">{row.label}</div>
            ) : (
              <NoteItem
                key={row.key}
                note={row.item.note}
                parent={row.item.parent}
                grouped={row.grouped}
                editingId={editingId}
                mentionableUsers={users}
                mentionableResources={resources}
                onSaveEdit={saveEdit}
                onCancelEdit={() => setEditingId(null)}
                onOpenActions={setActionNote}
                onToggleReaction={toggleReaction}
                onResourceClick={openResource}
                onOpenFile={onOpenFile}
              />
            )))}
          </>
        )}
        </div>
      </div>

      <div className="note-composer" ref={composerRef}>
        {replyTo && (
          <div className="note-reply-to">
            Replying to <b>{replyTo.who || replyTo.author?.full_name || 'a note'}</b>
            <button type="button" onClick={() => setReplyTo(null)} aria-label="Cancel reply">✕</button>
          </div>
        )}

        {!composing ? (
          <button type="button" className="note-composer-collapsed" onClick={() => setComposing(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            Write a note…
          </button>
        ) : (
          <>
            {files.length > 0 && (
              <div className="note-pending-files">
                {files.map((f) => (
                  <div key={f.uid} className="note-pending-file">
                    {f.isImage ? <img src={f.preview} alt={f.file.name} /> : null}
                    <span>{f.file.name}</span>
                    <button type="button" onClick={() => dropFile(f.uid)} aria-label="Remove file">✕</button>
                  </div>
                ))}
              </div>
            )}

            <RichNoteEditorLazy
              placeholder="Write a note… @ mentions HQ, / links a receipt"
              mentionableUsers={users}
              mentionableResources={resources}
              resetSignal={resetSignal}
              autoFocus
              onChange={(json, text) => setDraft({ json, text })}
              onSubmit={post}
            />

            <div className="note-composer-actions">
              <input ref={fileRef} type="file" multiple hidden onChange={pickFiles} />
              <button type="button" className="note-icon-btn" onClick={() => fileRef.current?.click()} aria-label="Attach files">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 11l-8.5 8.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L16 6" /></svg>
              </button>
              <span className="note-composer-hint" />
              <button type="button" className="note-icon-btn note-btn-text" onClick={() => guard.confirmClose(closeComposer)}>Cancel</button>
              <button type="button" className="note-icon-btn note-btn-text note-btn-primary" disabled={!canPost} onClick={post}>
                {posting ? 'Posting…' : 'Post'}
              </button>
            </div>
          </>
        )}
      </div>

      <NoteActionSheet
        note={actionNote}
        onClose={() => setActionNote(null)}
        onReact={toggleReaction}
        onReply={startReply}
        onEdit={(n) => setEditingId(n.id)}
        onCopy={copyNote}
        onDelete={deleteNote}
      />
    </div>
  );
}
